import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { ProfileData } from "@/hooks/useProfileSettings";
import PersonalInfoForm from "./profile/PersonalInfoForm";
import MessagingPlatformSelector from "./profile/MessagingPlatformSelector";
import LanguageSelector from "./LanguageSelector";
import { useTelegramConnection } from "@/hooks/useTelegramConnection";
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";

interface GeneralSettingsProps {
  profile: ProfileData | null;
  loading: boolean;
  connectingTelegram: boolean;
  connectionAttempted?: boolean;
  onUpdate: (updates: Partial<ProfileData>) => Promise<boolean>;
  onConnectTelegram: () => Promise<void>;
}

const GeneralSettings = ({
  profile,
  loading,
  connectingTelegram,
  connectionAttempted = false,
  onUpdate,
  onConnectTelegram
}: GeneralSettingsProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    firstName: profile?.first_name || "",
    lastName: profile?.last_name || "",
    country: profile?.country || "Kenya",
    currency: profile?.currency || "KES",
    phone: profile?.whatsapp_number || "",
    preferredMessaging: profile?.preferred_messaging || "whatsapp"
  });
  
  const { openTelegramDirectly } = useTelegramConnection();
  const location = useLocation();
  const missingFields: string[] = location.state?.missingFields || [];
  const refs = {
    "First Name": useRef<HTMLInputElement>(null),
    "Last Name": useRef<HTMLInputElement>(null),
    "Country": useRef<HTMLInputElement>(null),
    "Currency": useRef<HTMLInputElement>(null),
    "Phone Number": useRef<HTMLInputElement>(null),
    "Preferred Messaging": useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    if (missingFields.length > 0 && refs[missingFields[0]]?.current) {
      refs[missingFields[0]].current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [missingFields]);

  const handleFieldChange = (field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferredMessagingChange = async (value: string) => {
    await onUpdate({
      preferred_messaging: value
    });
    
    setFormState(prev => ({
      ...prev,
      preferredMessaging: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onUpdate({
      first_name: formState.firstName,
      last_name: formState.lastName,
      country: formState.country,
      currency: formState.currency,
      whatsapp_number: formState.phone,
      preferred_messaging: formState.preferredMessaging
    });
    setIsSaving(false);
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <PersonalInfoForm
          profile={profile}
          onFieldChange={handleFieldChange}
        />
        
        <MessagingPlatformSelector
          profile={profile}
          isLoading={loading}
          connectingTelegram={connectingTelegram}
          connectionAttempted={connectionAttempted}
          onPlatformChange={handlePreferredMessagingChange}
          onConnectTelegram={onConnectTelegram}
          onOpenTelegramDirectly={openTelegramDirectly}
        />
        
        <LanguageSelector />
        
        <Button 
          className="mt-4 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 transition-opacity text-white"
          onClick={handleSave}
          disabled={loading || isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('settings.saving')}
            </>
          ) : t('settings.saveChanges')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GeneralSettings;
